import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSignalR } from '@hooks/useSignalR';

interface ToastData {
  id: string;
  title: string;
  body: string;
  category: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
}

const categoryIcons: Record<string, string> = {
  housekeeping: '🛎️',
  maintenance: '🔧',
  kitchen: '🍽️',
  orders: '🍽️',
  breakfast: '☕',
  dining: '🍴',
  messaging: '💬',
  vouchers: '🎫',
};

/**
 * Global toast that appears at top of screen when a NotificationReceived
 * event comes through SignalR. Auto-dismisses after 5 seconds.
 * Tapping navigates to relevant page.
 */
export default function NotificationToast() {
  const { on } = useSignalR();
  const { hotelSlug } = useParams<{ hotelSlug: string }>();
  const navigate = useNavigate();
  const [toast, setToast] = useState<ToastData | null>(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => setToast(null), 300); // wait for slide-out animation
  }, []);

  const handleTap = useCallback(() => {
    if (!toast) return;
    dismiss();

    // Navigate to relevant page based on category
    const base = hotelSlug ? `/${hotelSlug}` : '';
    switch (toast.category) {
      case 'housekeeping':
        navigate(`${base}/housekeeping`);
        break;
      case 'maintenance':
        navigate(`${base}/maintenance`);
        break;
      case 'vouchers':
        navigate(`${base}/vouchers`);
        break;
      case 'orders':
      case 'breakfast':
      case 'dining':
      case 'kitchen':
        // If we have the related order id, go to order page
        if (toast.relatedEntityType === 'Order' && toast.relatedEntityId) {
          navigate(`${base}/order/${toast.relatedEntityId}`);
        } else {
          navigate(`${base}/notifications`);
        }
        break;
      default:
        navigate(`${base}/notifications`);
    }
  }, [toast, hotelSlug, navigate, dismiss]);

  useEffect(() => {
    const unsub = on('NotificationReceived', (data: Record<string, unknown>) => {
      // Clear existing timer
      if (timerRef.current) clearTimeout(timerRef.current);

      setToast({
        id: (data.id as string) || Date.now().toString(),
        title: (data.title as string) || 'Notification',
        body: (data.body as string) || '',
        category: (data.category as string) || '',
        relatedEntityType: data.relatedEntityType as string | undefined,
        relatedEntityId: data.relatedEntityId as string | undefined,
      });

      // Small delay so DOM can render before adding visible class
      requestAnimationFrame(() => setVisible(true));

      timerRef.current = setTimeout(dismiss, 5000);
    });

    return () => {
      unsub();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [on, dismiss]);

  if (!toast) return null;

  return (
    <div
      className={`notification-toast ${visible ? 'notification-toast--visible' : ''}`}
      onClick={handleTap}
      role="alert"
    >
      <span className="notification-toast-icon">
        {categoryIcons[toast.category] || '🔔'}
      </span>
      <div className="notification-toast-content">
        <strong className="notification-toast-title">{toast.title}</strong>
        <p className="notification-toast-body">{toast.body}</p>
      </div>
      <button
        className="notification-toast-close"
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
